import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import OrdersInformation from '@/lib/components/OrdersInformation';
import PaymentHeader from '@/lib/components/PaymentHeader';
import ThreeDotsIcon from '@/lib/assets/threeDots.svg';
import Instalments from '@/lib/components/Instalments';
import Loader from '@/lib/components/Loader';
import ErrorBlock from '@/lib/components/ErrorBlock';
import useLocale from '@/payment/useLocale';


import styles from './PaymentPlan.module.scss';
import { useIsSandboxMode } from '@/lib/hooks/useIsSandboxMode.js';
import ExclamationMarkIcon from '@/lib/assets/mark.svg';
import CrossIcon from '@/lib/assets/cross.svg';
import DisplayStatus from '@/payment/PaymentPlanPage/components/DisplayStatus.jsx';

const PaymentPlanPage = () => {
    const [isMenuVisible, setIsMenuVisible] = React.useState(false);
    const [data, setData] = React.useState();
    const [error, setError] = React.useState();
    const [isLoading, setIsLoading] = React.useState(true);
    const [displayConfirmCancelPlan, setDisplayConfirmCancelPlan] = React.useState(false);
    const [displayReturnButton, setDisplayReturnButton] = React.useState(false);
    const [confirmCancelPlanTimer, setConfirmCancelPlanTimer] = React.useState(15);
    const { t } = useTranslation();
    const isSandboxMode = useIsSandboxMode();
    const targetWeezConfig = isSandboxMode ? 'lyriaSandboxUrl' : 'lyriaUrl';
    const lyriaUrl = window.weezConfig?.[targetWeezConfig] ?? 'http://localhost:8003';
    const { setLocaleOption } = useLocale();

    React.useEffect(() => {
        let timer;
        if (displayConfirmCancelPlan) {
            setConfirmCancelPlanTimer(15);
            const interval = setInterval(() => {
                setConfirmCancelPlanTimer((prev) => prev - 1);
            }, 1000);
            timer = setTimeout(() => {
                setDisplayConfirmCancelPlan(false);
            }, 15000);
            return () => {
                clearTimeout(timer);
                clearInterval(interval);
            };
        }
    }, [displayConfirmCancelPlan]);

    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        // if it's a redirect after checkout -> display return button
        setDisplayReturnButton(urlParams.get('redirect'));
    }, [setDisplayReturnButton]);

    const onClickMenu = React.useCallback(() => {
        setIsMenuVisible(!isMenuVisible);
    }, [isMenuVisible]);

    const nextInstalmentToPay = data?.instalments_schedule?.find(instalment => instalment.status !== 'V');
    const amountPaid = ((data?.total_amount - data?.remaining_amount) / 100).toFixed(2);
    const planId = window.location.pathname.split('/')[2];

    const filterInstalments = (instalments) => {
        const instalmentMap = {};
        instalments.forEach(instalment => {
            const index = instalment.instalment_index;
            if (!instalmentMap[index]) {
                instalmentMap[index] = instalment;
            } else {
                const existing = instalmentMap[index];
                if (instalment.status === 'V') {
                    instalmentMap[index] = instalment;
                } else if (existing.status !== 'V' && instalment.status === 'A') {
                    if (new Date(instalment.attempted_at) > new Date(existing.attempted_at)) {
                        instalmentMap[index] = instalment;
                    }
                }
            }
        });
        return Object.values(instalmentMap).sort((a, b) => a.instalment_index - b.instalment_index);
    };

    const filteredInstalments = filterInstalments(
        [
            ...(data?.instalments_history || []),
            ...(data?.instalments_schedule || []),
        ],
    );

    const isPlanCancelled = ['A', 'T'].includes(data?.status);

    const redirectToInstalmentRequest = React.useCallback((processingInstalmentRequestUuid) => {
        const url = new URL(`${data?.payment_page_url}/${processingInstalmentRequestUuid}`);
        window.top.location.href = url.href;
    }, [data?.payment_page_url]);

    React.useEffect(() => {
        axios({
            method: 'GET',
            url: `${lyriaUrl}/instalment_plan/overview/${planId}/`,
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((data) => {
                setData(data.data);

                // Priority: lng query param > API response locale
                const urlParams = new URLSearchParams(window.location.search);
                const lngParam = urlParams.get('lng');
                const localeToUse = lngParam || data.data.locale;

                setLocaleOption(localeToUse);

            }).catch(
                error => setError(error.message),
            ).finally(
                () => setIsLoading(false),
            );
    }, [lyriaUrl, planId, setLocaleOption]);

    const totalAmount = t('price.' + data?.currency, {
        count: (data?.total_amount / 100).toFixed(2),
    });

    const cancelPaymentPlan = React.useCallback(async () => {
        setIsLoading(true);
        try {
            await axios({
                method: 'POST',
                url: `${lyriaUrl}/instalment_plan/plan/${planId}/cancel/`,
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const response = await axios({
                method: 'GET',
                url: `${lyriaUrl}/instalment_plan/overview/${planId}/`,
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            setData(response.data);
        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [lyriaUrl, planId]);

    const returnButtonClick = React.useCallback(() => {
        if (['validated', 'V', 'pending', 'P', 'waiting', 'W'].includes(data?.status)) {
            window.location.href = data?.callback_urls?.success;
        } else {
            window.location.href = data?.callback_urls?.failure;
        }
    }, [data]);

    // display overdue warning message when:
    const displayOverdueWarningMessage =
        data?.instalments_overdue &&
        data?.instalments_overdue?.amount > 0 && // there is overdue amount
        data?.instalments_schedule?.length > 0 && // there is still upcoming schedule instalment
        data?.instalments_schedule[0]?.instalment_index !== data?.total_instalments; // it's not the last instalment

    const getOverdueAmountWarningMessage = (overdueAmount, currency) => {
        const formattedDueAmount = t(`price.${currency}`, { count: overdueAmount });
        return (
            <p>
                {t('warningPlanOverdueAmount', { due_amount: formattedDueAmount })}
            </p>
        );
    };


    return (
        <div className={styles.body}>
            <PaymentHeader title='paymentPlan' />
            <div className={styles.content}>
                {isLoading && <Loader />}
                {error && <ErrorBlock errorCode='retryLater' />}
                {data &&
                    <>
                        {displayReturnButton && (
                            <div>
                                <button className={styles.return_button} onClick={returnButtonClick}>
                                    <span className={styles.return_button_arrow}>&lt;</span>{t('returnLabel')}
                                </button>
                            </div>
                        )}
                        <div className={styles.payment_plan_header}>
                            <div className={styles.top}>
                                <h2>{t('paymentPlanSummary')}</h2>
                                {data?.actions?.includes('cancel') ?
                                    <div className={styles.dot_icon} onClick={onClickMenu}>
                                        <ThreeDotsIcon />
                                        <div className={styles.menu_dropdown} hidden={!isMenuVisible}>
                                            <button
                                                className={styles.menu_item}
                                                onClick={() => {
                                                    setDisplayConfirmCancelPlan(true);
                                                }}>
                                                {t('cancelPaymentPlan')}
                                            </button>
                                        </div>
                                    </div>
                                    :
                                    <DisplayStatus status={data?.status} />
                                }
                            </div>
                            <div className={styles.middle}>
                                <p>{t('paidInstalments')}</p>
                                <p>{t('totalAmount')}</p>
                            </div>
                            <div className={styles.bottom}>
                                <p className={styles.bottom_left}>
                                    <span className={styles.fraction}>{data?.instalments_paid}/{data?.total_instalments} {t('paid').toLowerCase()}</span>
                                    (
                                    <span className={styles.amount_paid}>
                                        {t('price.' + data?.currency, {
                                            count: filteredInstalments
                                                ? amountPaid
                                                : 0,
                                        })}
                                    </span> &nbsp;/ {totalAmount}
                                    )
                                </p>
                                <p className={styles.total_amount}>
                                    {totalAmount}
                                </p>
                            </div>
                        </div>
                        {data?.actions?.includes('cancel') && displayConfirmCancelPlan && (
                            <div className={styles.warning_message}>
                                <CrossIcon
                                    className={styles.close_button}
                                    onClick={() => {
                                        setDisplayConfirmCancelPlan(false);
                                    }}
                                    title="Close warning"
                                />
                                <div className={styles.header}>
                                    <ExclamationMarkIcon className={styles.icon} />
                                    <p>
                                        {data?.organizer_accepts_refunds_on_payment_plan
                                            ? t('warningPlanConfirmCancel')
                                            : t('warningPlanCancelNotRefundable')}
                                    </p>
                                    <button
                                        className={styles.button}
                                        onClick={cancelPaymentPlan}
                                    >
                                        {t('confirm')} <span className={styles.small_text}>({confirmCancelPlanTimer}s)</span>
                                    </button>
                                </div>
                            </div>
                        )}
                        {!isPlanCancelled && displayOverdueWarningMessage && (
                            <div className={styles.warning_message}>
                                <div className={styles.header}>
                                    <ExclamationMarkIcon className={styles.icon} />
                                    {getOverdueAmountWarningMessage(data?.instalments_overdue?.amount, data?.instalments_overdue?.currency)}
                                </div>
                            </div>
                        )}
                        <Instalments
                            lyriaUrl={lyriaUrl}
                            instalmentPlanId={data?.id}
                            intalmentPlanStatus={data?.status}
                            instalments={filteredInstalments}
                            currency={data?.currency}
                            nextInstalmentToPay={nextInstalmentToPay}
                            processingInstalmentRequestUuid={data?.next_payment_uuid}
                            processingInstalmentRequestIndex={data?.processing_instalment_request_index}
                            redirectToInstalmentRequest={redirectToInstalmentRequest}
                            setError={setError}
                        />
                        <OrdersInformation reference={data?.reference} />
                    </>
                }
            </div>
        </div>
    );
};

export default PaymentPlanPage;
